import { useCallback } from 'react';
import { log } from 'log';
import { prop } from 'ramda';
import { useGetURLTemplate } from 'server';
import { useUserSelector } from 'js/store/hooks';
import { getBaseDomain } from 'js/utils/url.util';
import { useGetTemporaryAuthenticationToken } from 'js/hooks/auth';

import { useGameData } from './useGameData';

export const useGetLauncherUrl = () => {
    const getURLTemplate = useGetURLTemplate();
    const lang = useUserSelector(prop('lang'));
    const {
        urlType,
        preview,
        gameName,
        gameCode,
        mobileGameCode,
        integrationType,
        liveLaunchAlias,
        urlCustomParameters,
        additionalQueryString
    } = useGameData();
    const getTemporaryAuthenticationToken = useGetTemporaryAuthenticationToken();

    return useCallback(async () => {
        let launchUrlStr = await getURLTemplate({ urlTypeList: [urlType] });

        if (!launchUrlStr) {
            return '';
        }

        launchUrlStr = launchUrlStr
            .replace('[superdomain]', getBaseDomain())
            .replace(/\[gamename]/g, gameName)
            .replace(/\[gamecode]/g, gameCode)
            .replace(/\[mobilegamecode]/g, mobileGameCode)
            .replace(/\[integrationtype]/g, integrationType)
            .replace(/\[livelaunchalias]/g, liveLaunchAlias)
            .replace(/\[language]/g, lang.slice(0, 2))
            .replace(/\[back_url]/g, location.origin);

        if (launchUrlStr.includes('[temptoken]')) {
            const token = await getTemporaryAuthenticationToken();

            launchUrlStr = launchUrlStr
                .replace(/\[temptoken]/g, token);
        }

        const launchUrl = new window.URL(launchUrlStr);

        if (preview) {
            launchUrl.searchParams.append('preview', preview);
        }

        // Query string from CMS game config
        if (urlCustomParameters) {
            try {
                const additionalUrlCustomParameters = new window.URLSearchParams(urlCustomParameters);

                additionalUrlCustomParameters.forEach((value, key) => {
                    launchUrl.searchParams.set(key, value);
                });
            } catch {
                log('error', `Game configuration error: check urlCustomParameters for game "${gameName}"`);
            }
        }

        // Games portlet query string
        if (additionalQueryString) {
            try {
                const additionalQueryParams = new window.URLSearchParams(additionalQueryString);

                additionalQueryParams.forEach((value, key) => {
                    launchUrl.searchParams.set(key, value);
                });
            } catch {
                log('error', `Cannot parse additionalQueryString for game "${gameName}"`);
            }
        }

        return launchUrl.toString();
    }, [
        lang,
        urlType,
        preview,
        gameName,
        gameCode,
        getURLTemplate,
        mobileGameCode,
        integrationType,
        liveLaunchAlias,
        urlCustomParameters,
        additionalQueryString,
        getTemporaryAuthenticationToken
    ]);
};
